import { keyOf, parseKey, type Coord, type Seat } from '@nt2/engine';

/**
 * Where a player's own tiles are, as the `arrange` action takes it: which are
 * on the grid and where, and the order of the rest in the rack.
 *
 * Swaps and undo are both "put my tiles like this", so both are built here as
 * whole layouts and sent as one action. Doing a swap as a recall followed by a
 * place would pass through a moment with the square empty and the rack one
 * longer, which a draw arriving in between could make a mess of.
 */
export interface Layout {
  grid: Array<{ tileId: string; to: Coord }>;
  rack: string[];
}

/** Somewhere a tile can be: a square on the grid, or a slot in the rack. */
export type Spot = { grid: Coord } | { rack: number };

export function layoutOf(seat: Seat): Layout {
  return {
    grid: [...seat.grid].map(([k, t]) => ({ tileId: t.id, to: parseKey(k) })),
    rack: seat.rack.map((t) => t.id),
  };
}

export function sameLayout(a: Layout, b: Layout): boolean {
  if (a.rack.length !== b.rack.length || a.grid.length !== b.grid.length) return false;
  if (a.rack.some((id, i) => id !== b.rack[i])) return false;
  const at = new Map(a.grid.map((m) => [m.tileId, keyOf(m.to)]));
  return b.grid.every((m) => at.get(m.tileId) === keyOf(m.to));
}

/** Where a tile is now, or null if the seat does not hold it. */
export function spotOf(seat: Seat, tileId: string): Spot | null {
  const i = seat.rack.findIndex((t) => t.id === tileId);
  if (i >= 0) return { rack: i };
  for (const [k, t] of seat.grid) if (t.id === tileId) return { grid: parseKey(k) };
  return null;
}

/**
 * Exchange two of the seat's tiles, wherever each of them is.
 *
 * Rack and rack trades their slots; grid and grid trades their squares; rack
 * and grid puts the rack letter on the square and the grid letter in the rack
 * slot it came from.
 */
export function swapTiles(seat: Seat, a: string, b: string): Layout | null {
  const sa = spotOf(seat, a), sb = spotOf(seat, b);
  if (!sa || !sb || a === b) return null;
  const out = layoutOf(seat);
  const put = (id: string, spot: Spot): void => {
    if ('rack' in spot) out.rack[spot.rack] = id;
    else out.grid.push({ tileId: id, to: spot.grid });
  };
  out.grid = out.grid.filter((m) => m.tileId !== a && m.tileId !== b);
  put(a, sb);
  put(b, sa);
  return out;
}

/**
 * Move one tile to a free square, or into the rack before slot `index`.
 * Returns null when the square is taken by another tile.
 */
export function moveTile(seat: Seat, tileId: string, to: Spot): Layout | null {
  if (!spotOf(seat, tileId)) return null;
  const out = layoutOf(seat);
  out.grid = out.grid.filter((m) => m.tileId !== tileId);
  if ('grid' in to) {
    if (out.grid.some((m) => keyOf(m.to) === keyOf(to.grid))) return null;
    out.rack = out.rack.filter((id) => id !== tileId);
    out.grid.push({ tileId, to: to.grid });
    return out;
  }
  // Inserting before a slot: if the tile was ahead of it in the rack, taking
  // it out shifts that slot one to the left.
  const was = out.rack.indexOf(tileId);
  let at = to.rack;
  if (was >= 0 && was < at) at--;
  out.rack = out.rack.filter((id) => id !== tileId);
  out.rack.splice(Math.max(0, Math.min(at, out.rack.length)), 0, tileId);
  return out;
}

/**
 * Fit a remembered layout to the tiles the seat holds now.
 *
 * Between remembering and restoring, a draw may have added letters and a
 * take-two may have removed one. Tiles that are gone are dropped from it;
 * tiles it never knew about stay in the rack, after the ones it does.
 */
export function fitLayout(seat: Seat, layout: Layout): Layout {
  const held = new Set([...seat.rack.map((t) => t.id), ...[...seat.grid.values()].map((t) => t.id)]);
  const grid = layout.grid.filter((m) => held.has(m.tileId));
  const rack = layout.rack.filter((id) => held.has(id));
  const named = new Set([...grid.map((m) => m.tileId), ...rack]);
  for (const t of seat.rack) if (!named.has(t.id)) rack.push(t.id);
  for (const t of seat.grid.values()) if (!named.has(t.id)) rack.push(t.id);
  return { grid, rack };
}

/**
 * Undo and redo for rearranging your own tiles.
 *
 * What is remembered is the layout *before* each change, not the change
 * itself. Undoing is then "put things back as they were", which stays right
 * even when the server has since dealt new letters, and needs no inverse for
 * each kind of move.
 */
export class LayoutHistory {
  static readonly LIMIT = 100;
  #back: Layout[] = [];
  #forward: Layout[] = [];

  get canUndo(): boolean { return this.#back.length > 0; }
  get canRedo(): boolean { return this.#forward.length > 0; }

  /** Call just before a change the player made. */
  record(before: Layout): void {
    const last = this.#back[this.#back.length - 1];
    if (last && sameLayout(last, before)) return;
    this.#back.push(before);
    if (this.#back.length > LayoutHistory.LIMIT) this.#back.shift();
    this.#forward = [];
  }

  /** The layout to restore, or null. Steps that would change nothing are skipped. */
  undo(now: Layout): Layout | null {
    return this.#step(this.#back, this.#forward, now);
  }

  redo(now: Layout): Layout | null {
    return this.#step(this.#forward, this.#back, now);
  }

  clear(): void { this.#back = []; this.#forward = []; }

  #step(from: Layout[], to: Layout[], now: Layout): Layout | null {
    while (from.length) {
      const next = from.pop()!;
      if (sameLayout(next, now)) continue;
      to.push(now);
      return next;
    }
    return null;
  }
}
